import {
  IAMUserState,
  JsonSchema,
  JsonSchemaPropertyMap,
  RowData,
  ToastInfo,
} from "../../../../types";
import { TOAST_MESSAGES } from "../../constants";
import { mapExcelRows } from "./mapExcelRows";
import { getAllFieldProperties, getFieldTitles } from "./schemaHelpers";
import * as XLSX from "xlsx";

const validateExcelHeaders = (
  headers: string[],
  schema: JsonSchema,
  correlationProperties: JsonSchemaPropertyMap,
): ToastInfo | undefined => {
  const requiredHeaders = getFieldTitles(
    getAllFieldProperties(schema, correlationProperties),
  );
  const missingHeaders = Object.values(requiredHeaders).filter(
    (header) => !headers.includes(header),
  );

  return missingHeaders.length
    ? TOAST_MESSAGES.MISSING_IMPORT_COLUMNS(missingHeaders.join(", "))
    : undefined;
};

export const parseCorelationExcelFile = async (
  file: File,
  schema: JsonSchema,
  title: string,
  corelationFieldProperties: JsonSchemaPropertyMap,
  iamUserState: IAMUserState,
): Promise<{
  data: RowData[];
  fileParseError?: ToastInfo;
}> => {
  try {
    const workbook = XLSX.read(await file.arrayBuffer(), {
      type: "array",
    });

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    if (sheetName !== title) {
      return {
        data: [],
        fileParseError: TOAST_MESSAGES.SHEET_NAME_MISMATCH(title),
      };
    }

    const jsonData = XLSX.utils.sheet_to_json<RowData>(sheet);
    const headers = XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
    });

    const headerError = validateExcelHeaders(
      headers[0] as string[],
      schema,
      corelationFieldProperties,
    );

    if (headerError) {
      return {
        data: [],
        fileParseError: headerError,
      };
    }

    if (!jsonData.length) {
      return {
        data: [],
        fileParseError: TOAST_MESSAGES.EMPTY_FILE(),
      };
    }

    return {
      data: mapExcelRows(
        jsonData,
        schema,
        corelationFieldProperties,
        iamUserState,
      ),
    };
  } catch {
    return {
      data: [],
      fileParseError: TOAST_MESSAGES.FILE_PARSE_ERROR(),
    };
  }
};
